import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Label,
  Switch,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss12Filled,
  Image24Regular,
  Map24Regular,
} from '@fluentui/react-icons';
import ContentSkeleton from './ContentSkeleton/ContentSkeleton';

export interface ImagePreviewContent {
  imageContent: string;
  location?: { latitude: number; longitude: number } | null;
  note?: string;
  analysis?: string | null;
}

export interface ImagePreviewDialogLabels {
  title?: string;
  close?: string;
  loading?: string;
  location?: string;
  note?: string;
  showAnalytics?: string;
  hideAnalytics?: string;
  objects?: string;
  persons?: string;
  texts?: string;
}

interface IAnalytics {
  caption?: string;
  objects?: Array<{
    name: string;
    confidence: number;
    box: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  }>;
  people?: Array<{
    confidence: number;
    box: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  }>;
  readBlocks?: Array<{
    text: string;
    polygon: Array<{
      x: number;
      y: number;
    }>;
  }>;
}

export interface ImagePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  content?: ImagePreviewContent | null;
  loading?: boolean;
  error?: string | null;
  captionsSrc?: string;
  labels?: ImagePreviewDialogLabels;
}

const useStyles = makeStyles({
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    ...shorthands.padding('20px'),
  },
  modalContent: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('24px'),
    width: '600px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    minWidth: '400px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
  },
  modalCloseButton: {
    minWidth: '32px',
    width: '32px',
    height: '32px',
  },
  modalBody: {
    marginBottom: '16px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.gap('8px'),
  },
  mediaContainer: {
    textAlign: 'center',
    marginBottom: '16px',
    height: '350px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalImage: {
    maxWidth: '100%',
    maxHeight: '350px',
    width: 'auto',
    height: 'auto',
    ...shorthands.borderRadius('8px'),
  },
  modalVideo: {
    maxWidth: '100%',
    maxHeight: '350px',
    width: 'auto',
    height: 'auto',
    ...shorthands.borderRadius('8px'),
  },
  analyticsWrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  analyticsCaption: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    color: '#fff',
    padding: '8px 12px',
    fontSize: '18px',
    textAlign: 'center',
  },
  locationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
  },
  locationText: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  locationIcon: {
    color: tokens.colorBrandForeground1,
  },
  mapToggleButton: {
    minWidth: '32px',
    width: '32px',
    height: '32px',
    marginLeft: 'auto',
  },
});

const defaultLabels: Required<ImagePreviewDialogLabels> = {
  title: 'Image Preview',
  close: 'Close',
  loading: 'Loading',
  location: 'Location',
  note: 'Note',
  showAnalytics: 'Show Analytics',
  hideAnalytics: 'Hide Analytics',
  objects: 'Objects',
  persons: 'Persons',
  texts: 'Texts',
};

const isVideoContent = (value?: string | null) => {
  if (!value) return false;
  if (value.startsWith('data:video')) return true;
  const sanitized = value.split('?')[0].toLowerCase();
  return sanitized.endsWith('.webm') || sanitized.endsWith('.mp4');
};

export const ImagePreviewDialog = ({
  open,
  onClose,
  content,
  loading = false,
  error,
  captionsSrc,
  labels,
}: ImagePreviewDialogProps) => {
  const styles = useStyles();
  const mergedLabels = useMemo(
    () => ({ ...defaultLabels, ...labels }),
    [labels]
  );

  const [imageAnalytics, setImageAnalytics] = useState<IAnalytics | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showObjects, setShowObjects] = useState(true);
  const [showPersons, setShowPersons] = useState(true);
  const [showTexts, setShowTexts] = useState(true);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!content?.analysis) {
      setShowAnalytics(false);
      setImageAnalytics(null);
    }
  }, [content]);

  useEffect(() => {
    if (!content?.location) {
      setShowMap(false);
    }
  }, [content]);

  const mapUrl = content?.location
    ? `https://www.google.com/maps?q=${content.location.latitude},${content.location.longitude}&z=16&output=embed`
    : '';

  const handleSwitchAnalytics = (_: any, data: { checked: boolean }) => {
    if (data.checked && content?.analysis) {
      const analysis: IAnalytics = JSON.parse(content.analysis);
      setImageAnalytics(analysis);
      setShowAnalytics(true);
    } else {
      setShowAnalytics(false);
      setImageAnalytics(null);
    }
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{mergedLabels.title}</h2>
          <Button
            icon={<Dismiss12Filled />}
            onClick={onClose}
            appearance="subtle"
            className={styles.modalCloseButton}
            title={mergedLabels.close}
          />
        </div>
        <div className={styles.modalBody}>
          {loading && (
            <ContentSkeleton variant="lines" label={mergedLabels.loading} />
          )}
          {error && <div style={{ color: 'red' }}>{error}</div>}
          {content && (
            <div>
              <div className={styles.mediaContainer}>
                {showMap && content.location ? (
                  <iframe
                    title="location-map"
                    src={mapUrl}
                    style={{
                      width: '100%',
                      height: '350px',
                      border: 0,
                      borderRadius: 8,
                    }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : isVideoContent(content.imageContent) ? (
                  <video
                    controls
                    className={styles.modalVideo}
                    src={content.imageContent}
                  >
                    {captionsSrc && (
                      <track
                        kind="captions"
                        src={captionsSrc}
                        label="English"
                      />
                    )}
                  </video>
                ) : (
                  <>
                    {imageAnalytics ? (
                      <div className={styles.analyticsWrapper}>
                        <img
                          src={content.imageContent}
                          alt={mergedLabels.title}
                          className={styles.modalImage}
                          style={{ display: 'block' }}
                        />
                        <svg
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                          }}
                          width="100%"
                          height="100%"
                          viewBox="0 0 640 480"
                        >
                          {showObjects &&
                            Array.isArray(imageAnalytics.objects) &&
                            imageAnalytics.objects.map((obj, idx) => (
                              <g key={`obj-${idx}`}>
                                <rect
                                  x={obj.box.x}
                                  y={obj.box.y}
                                  width={obj.box.width}
                                  height={obj.box.height}
                                  fill="none"
                                  stroke="#00f"
                                  strokeWidth="2"
                                />
                                <text
                                  x={obj.box.x + 4}
                                  y={obj.box.y + 18}
                                  fill="#00f"
                                  fontSize="16"
                                  fontWeight="bold"
                                  stroke="#fff"
                                  strokeWidth="0.5"
                                >
                                  {obj.name} (
                                  {(obj.confidence * 100).toFixed(1)}%)
                                </text>
                              </g>
                            ))}
                          {showPersons &&
                            Array.isArray(imageAnalytics.people) &&
                            imageAnalytics.people.map((person, idx) => (
                              <g key={`person-${idx}`}>
                                <rect
                                  x={person.box.x}
                                  y={person.box.y}
                                  width={person.box.width}
                                  height={person.box.height}
                                  fill="none"
                                  stroke="#0a0"
                                  strokeDasharray="4 2"
                                  strokeWidth="2"
                                />
                                <text
                                  x={person.box.x + 4}
                                  y={person.box.y + 18}
                                  fill="#0a0"
                                  fontSize="16"
                                  fontWeight="bold"
                                  stroke="#fff"
                                  strokeWidth="0.5"
                                >
                                  Person ({(person.confidence * 100).toFixed(1)}
                                  %)
                                </text>
                              </g>
                            ))}
                          {showTexts &&
                            Array.isArray(imageAnalytics.readBlocks) &&
                            imageAnalytics.readBlocks.map((block, idx) => (
                              <g key={`readblock-${idx}`}>
                                {block.polygon && block.polygon.length > 1 && (
                                  <polygon
                                    points={block.polygon
                                      .map((pt) => `${pt.x},${pt.y}`)
                                      .join(' ')}
                                    fill="rgba(255,255,0,0.2)"
                                    stroke="#fa0"
                                    strokeWidth="2"
                                  />
                                )}
                                {block.polygon && block.polygon.length > 0 && (
                                  <text
                                    x={block.polygon[0].x}
                                    y={block.polygon[0].y - 4}
                                    fill="#fa0"
                                    fontSize="14"
                                    fontWeight="bold"
                                    stroke="#fff"
                                    strokeWidth="0.5"
                                  >
                                    {block.text}
                                  </text>
                                )}
                              </g>
                            ))}
                        </svg>
                        {imageAnalytics.caption && (
                          <div className={styles.analyticsCaption}>
                            {imageAnalytics.caption}
                          </div>
                        )}
                      </div>
                    ) : (
                      <img
                        src={content.imageContent}
                        alt={mergedLabels.title}
                        className={styles.modalImage}
                      />
                    )}
                  </>
                )}
              </div>
              {content.location && (
                <p className={styles.locationRow}>
                  <span className={styles.locationText}>
                    <Text>
                      {mergedLabels.location}: {content.location.latitude},{' '}
                      {content.location.longitude}
                    </Text>
                  </span>
                  <Button
                    appearance="subtle"
                    icon={
                      showMap ? (
                        <Image24Regular className={styles.locationIcon} />
                      ) : (
                        <Map24Regular className={styles.locationIcon} />
                      )
                    }
                    className={styles.mapToggleButton}
                    title={showMap ? 'Image' : 'Map'}
                    onClick={() => setShowMap((prev) => !prev)}
                  />
                </p>
              )}
              {content.note && (
                <p>
                  <Text>
                    {mergedLabels.note}: {content.note}
                  </Text>
                </p>
              )}
            </div>
          )}
        </div>
        <div className={styles.modalActions}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!showMap && showAnalytics && content?.analysis && (
              <>
                <Switch
                  checked={showObjects}
                  onChange={(_e, data) => setShowObjects(data.checked)}
                  label={
                    <Label style={{ color: '#00f' }}>
                      {mergedLabels.objects}
                    </Label>
                  }
                />
                <Switch
                  checked={showPersons}
                  onChange={(_e, data) => setShowPersons(data.checked)}
                  label={
                    <Label style={{ color: '#0a0' }}>
                      {mergedLabels.persons}
                    </Label>
                  }
                />
                <Switch
                  checked={showTexts}
                  onChange={(_e, data) => setShowTexts(data.checked)}
                  label={
                    <Label style={{ color: '#fa0' }}>
                      {mergedLabels.texts}
                    </Label>
                  }
                />
              </>
            )}
            <Switch
              checked={!!content?.analysis && showAnalytics}
              onChange={handleSwitchAnalytics}
              disabled={!content?.analysis || showMap}
              label={
                showAnalytics
                  ? mergedLabels.hideAnalytics
                  : mergedLabels.showAnalytics
              }
            />
            <Button appearance="primary" onClick={onClose}>
              {mergedLabels.close}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
